import type { BatchProcessItemDto } from "@fp/admin-api-client";
import {
  BatchProcessItemOperation,
  BatchProcessItemStatus,
} from "@fp/admin-api-client";
import { Badge } from "@fp/ui/components/base/badges/badges";
import { Loading02 } from "@untitledui/icons";
import { type ReactNode, useEffect, useRef } from "react";
import { getBatchItemTitle } from "./batch-item-title";
import { getDisplayedItemStatus } from "./batch-process-item-pacing";
import {
  getBatchItemStatusMeta,
  getOutcomeTypeLabel,
  isSettledStatus,
} from "./batch-process-status";
import { FsmExecuteSyncItemDetail } from "./fsm-execute-sync-item-detail";
import type { BatchProcessRun } from "./use-batch-process-run";

const itemDetailByOperation: Partial<
  Record<
    BatchProcessItemOperation,
    (item: BatchProcessItemDto) => ReactNode
  >
> = {
  [BatchProcessItemOperation.Preview]: (item) => <FsmExecuteSyncItemDetail item={item} />,
  [BatchProcessItemOperation.Sync]: (item) => <FsmExecuteSyncItemDetail item={item} />,
};

function BatchProcessItemRow({
  item,
  status,
}: {
  item: BatchProcessItemDto;
  status: BatchProcessItemStatus;
}) {
  const statusMeta = getBatchItemStatusMeta(status, item.operation);
  const isFailed = status === BatchProcessItemStatus.Failed;
  const isSettled = isSettledStatus(status);
  const hasOutcome =
    isSettled && Boolean(item.outcomeType || item.outcomeMessage);

  return (
    <li className="flex flex-col gap-1 px-4 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-sm text-secondary">
          {getBatchItemTitle(item)}
        </span>
        <Badge size="sm" color={statusMeta.color}>
          {statusMeta.label}
        </Badge>
      </div>
      {hasOutcome && (
        <div
          className={
            isFailed ? "text-xs text-error-primary" : "text-xs text-tertiary"
          }
        >
          {item.outcomeType && (
            <span className="font-medium">
              {getOutcomeTypeLabel(item.outcomeType)}
              {item.outcomeMessage ? ": " : ""}
            </span>
          )}
          {item.outcomeMessage}
        </div>
      )}
      {isSettled && itemDetailByOperation[item.operation]?.(item)}
    </li>
  );
}

export function BatchProcessItemList({ run }: { run: BatchProcessRun }) {
  const { items, statuses, isLoading, isError, hasMore, isLoadingMore } = run;
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = sentinel.current;

    if (!target || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        run.loadMore();
      }
    });

    observer.observe(target);

    return () => observer.disconnect();
  }, [hasMore, run.loadMore]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 px-4 py-4 text-sm text-tertiary">
        <Loading02 className="size-4 animate-spin" />
        Loading items…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-4 py-4 text-sm text-error-primary">
        Failed to load items.
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="px-4 py-4 text-sm text-tertiary">No items to show.</div>
    );
  }

  return (
    <div className="border-t border-secondary bg-secondary_alt">
      <ul className="divide-y divide-secondary">
        {items.map((item) => (
          <BatchProcessItemRow
            key={item.id}
            item={item}
            status={getDisplayedItemStatus(statuses, item.id, item.status)}
          />
        ))}
      </ul>
      {hasMore && (
        <div
          ref={sentinel}
          className="flex items-center justify-center gap-2 px-4 py-3 text-sm text-tertiary"
        >
          {isLoadingMore && <Loading02 className="size-4 animate-spin" />}
          Loading more…
        </div>
      )}
    </div>
  );
}